// src/component/TopPage.js
import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";

import MMHeader from "./MMHeader";
import MMInfoTabs from "./MMInfoTabs";
import TopPageMusicSection from "./TopPageMusicSection";
import MM2026JoinDialogLauncher from "./MM2026JoinDialogLauncher";
import GroupActivity2026DialogLauncher from "./GroupActivity2026DialogLauncher";
// import TopPageMusicSectionLite from "./TopPageMusicSectionLite";

import { AppThemeContext } from "./AppShell";

import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../api/firebase";
import { onAuthStateChanged } from "firebase/auth";
import BuildIdGuard from "./BuildIdGuard";

// 🔹 設定が無いときのフォールバック
const DEFAULT_TRACK = {
  id: "seasonal-track",
  title: "Seasonal MasterMind Track",
  src: "/audio/mm_seasonal_track.mp3",
};

export default function TopPage() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const softBg = theme.surfaceBg || "#FFFFFF";

  const [track, setTrack] = useState(DEFAULT_TRACK);
  const [loadingTrack, setLoadingTrack] = useState(true);

  // ★ uid を state に落とす（ログイン確定のトリガー）
  const [uid, setUid] = useState(null);

  // ★ users/{uid} を入れる
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);

  // ✅ 「ユーザ情報が全部入っているかどうか」
  const [canShowJoinLauncher, setCanShowJoinLauncher] = useState(false);

  // ✅ BuildIdGuard が allow かつ buildId 記録完了したら true になる
  //    これが true になるまで「重いFirestoreを読むUI」を出さない
  const [buildReady, setBuildReady] = useState(false);

  // ─────────────────────────────
  // Auth: uid を state 化
  // ─────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
    });
    return () => unsub();
  }, []);

  // uid が変わったら buildReady を戻す（ログアウト→別ユーザーなどの事故防止）
  useEffect(() => {
    setBuildReady(false);
  }, [uid]);

  // ─────────────────────────────
  // 🎵 Firestore から「トップページ用の曲」を取得
  // ─────────────────────────────
  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const ref = doc(db, "musicSettings", "topPageSeasonalTrack");
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
          setTrack({
            id: data.trackId || "seasonal-track",
            title: data.title || DEFAULT_TRACK.title,
            src: data.src || DEFAULT_TRACK.src,
          });
        } else {
          setTrack(DEFAULT_TRACK);
        }
      } catch (e) {
        console.error("Failed to load seasonal track:", e);
        setTrack(DEFAULT_TRACK);
      } finally {
        setLoadingTrack(false);
      }
    };

    fetchTrack();
  }, []);

  // ─────────────────────────────
  // users/{uid} を取得 → userInfo に入れる（uid が確定してから）
  // ─────────────────────────────
  useEffect(() => {
    let alive = true;

    const run = async () => {
      setLoadingUserInfo(true);

      if (!uid) {
        if (!alive) return;
        setUserInfo(null);
        setCanShowJoinLauncher(false);
        setLoadingUserInfo(false);
        return;
      }

      try {
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);

        if (!alive) return;

        if (!snap.exists()) {
          setUserInfo(null);
          setCanShowJoinLauncher(false);
          return;
        }

        const data = snap.data() || {};
        setUserInfo({ id: snap.id, ...data });

        const hasAllRequired =
          data.registrationName &&
          data.registrationFurigana &&
          data.registrationEmail &&
          data.registrationPhone &&
          data.prefecture;

        setCanShowJoinLauncher(Boolean(hasAllRequired));
      } catch (e) {
        console.error("Failed to load userInfo:", e);
        if (!alive) return;
        setUserInfo(null);
        setCanShowJoinLauncher(false);
      } finally {
        if (!alive) return;
        setLoadingUserInfo(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [uid]);

  return (
    <Box
      sx={{
        pt: 0.5,
        pb: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        minHeight: "100%",
        bgcolor: softBg,
        backgroundImage: `
          radial-gradient(circle at 0% 0%, ${accent}20 0, transparent 55%),
          radial-gradient(circle at 100% 100%, ${accent}10 0, transparent 60%)
        `,
        backgroundRepeat: "no-repeat",
        backgroundSize: "140% 140%",
        transition: "background 0.4s ease",
      }}
    >
      {canShowJoinLauncher && <MM2026JoinDialogLauncher />}
      {canShowJoinLauncher && <GroupActivity2026DialogLauncher />}

      {/* ★ Gate：BuildIdGuard が allow になり「buildId記録まで完了」したら buildReady を true にする */}
      <BuildIdGuard onReady={() => setBuildReady(true)} />

      {/* <MMHeader /> */}
      {/* ★重要：buildReady になるまで重いFirestoreを読むUIを出さない（旧JS残党のpermission-denied事故回避） */}
      {buildReady && !loadingTrack && <TopPageMusicSection track={track} />}


      <Box sx={{ mt: 4 }}>
        <MMInfoTabs />
      </Box>
    </Box>
  );
}
